<template>
	<div class="payment-page-container">
		<div class="payment-search-container">
			<input class="payment-search-input" type="text" v-model="this.searchKeyword"
			       placeholder="输入付款人邮箱 / 支付ID搜索" @keyup.enter="onSearchClicked">
			<div class="header-btn payment-search-btn" @click="onSearchClicked">搜索</div>
			<div class="header-btn payment-search-btn" @click="onResetSearchClicked">重置</div>
		</div>

		<div class="btn-container">
			<div class="payment-page-title">{{ this.getListTitle() }}</div>

			<a-pagination v-model:current="this.page" simple :pageSize="this.pageSize"
			              :total="this.totalCount" @change="onPageChanged"
			              :showSizeChanger="false"/>

			<div class="header-btn payment-search-btn" @click="onRefreshBtnClicked">刷新</div>
		</div>

		<!-- 状态筛选。两个都勾掉就什么都不显示，列表会给出提示，不用禁掉勾选框 -->
		<div class="payment-filter-container">
			<a-checkbox v-model:checked="this.showConfirmed" @change="onFilterChanged">显示已支付</a-checkbox>
			<a-checkbox v-model:checked="this.showUnconfirmed" @change="onFilterChanged">显示未支付</a-checkbox>
		</div>

		<div class="cs-main-container">
			<div class="list-container">
				<div class="payment-empty" v-if="this.isLoaded === true && this.paymentList.length === 0">
					{{ this.getEmptyText() }}
				</div>

				<!-- 两张表的 id 各自生成，拼上来源才保证 key 唯一 -->
				<template v-for="item in this.paymentList" :key="`${item.source}_${item.paymentId}`">
					<div class="item-container payment-row">
						<div class="payment-head-line">
							<div class="payment-head-info">
								<span class="payment-amount">{{ this.getAmountText(item) }}</span>
								<span class="payment-method">{{ item.payMethod || '未知支付方式' }}</span>
								<span :class="['payment-status-tag', item.isConfirmed === true ? 'is-paid' : 'is-unpaid']">
									{{ item.isConfirmed === true ? '已支付' : '未支付' }}
								</span>
								<span class="payment-refund-tag" v-if="item.isRefund === true">已退款</span>
							</div>

							<div class="payment-btn-group">
								<!-- 只有已支付的单才需要"退单+封人"这一组合，未支付的没什么可退 -->
								<a-popconfirm v-if="item.isConfirmed === true" :title="this.getBlockConfirmText(item)"
								              ok-text="确认" cancel-text="取消"
								              @confirm="onMarkUnpaidAndBlock(item)">
									<div :class="['header-btn', 'payment-toggle-btn', 'is-block',
									              this.updatingPaymentId === item.paymentId ? 'is-updating' : '']">
										设为未支付并Block
									</div>
								</a-popconfirm>

								<a-popconfirm :title="this.getToggleConfirmText(item)" ok-text="确认" cancel-text="取消"
								              @confirm="onToggleConfirmed(item)">
									<div :class="['header-btn', 'payment-toggle-btn',
									              item.isConfirmed === true ? 'is-to-unpaid' : 'is-to-paid',
									              this.updatingPaymentId === item.paymentId ? 'is-updating' : '']">
										{{ item.isConfirmed === true ? '设为未支付' : '设为已支付' }}
									</div>
								</a-popconfirm>
							</div>
						</div>

						<div class="payment-field-line">
							<span class="payment-field-label">支付时间</span>
							<span>{{ this.formatTimeString(item.paymentTime) }}</span>
						</div>

						<div class="payment-field-line">
							<span class="payment-field-label">付款人</span>
							<span>{{ item.email || '无' }}</span>
							<img src="../images/copy_light.svg" alt="复制" class="copy-text-btn"
							     title="复制邮箱" v-if="item.email"
							     @click="onCopyText(item.email, '邮箱')">
						</div>

						<div class="payment-field-line">
							<span class="payment-field-label">支付ID</span>
							<!-- 只显示头尾，完整值放 title 里，鼠标停上去能看全 -->
							<span class="payment-id-text" :title="item.paymentId">
								{{ this.maskPaymentId(item.paymentId) }}
							</span>
							<img src="../images/copy_light.svg" alt="复制" class="copy-text-btn"
							     title="复制支付ID" v-if="item.paymentId"
							     @click="onCopyText(item.paymentId, '支付ID')">
						</div>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
import {copyToClipboard, hideLoading, postRequestAsync, showLoading} from "@/utils/methods";
import moment from "moment-timezone";

export default {
	name: "PaymentPage",
	data() {
		return {
			paymentList: [],
			searchKeyword: '',
			// 当前列表是搜索结果还是全量列表，空列表的文案不一样
			isSearch: false,
			page: 1,
			pageSize: 20,
			totalCount: 0,
			// 区分"还没查"和"查完是空的"，否则一进页面就会闪一下空列表提示
			isLoaded: false,
			// 状态筛选，默认两种都看
			showConfirmed: true,
			showUnconfirmed: true,
			updatingPaymentId: null
		}
	},
	async mounted() {
		await this.getPaymentList()
	},
	methods: {
		async getPaymentList(isManual = false) {
			showLoading()
			const result = await postRequestAsync('admin/payment/getPaymentList', {
				keyword: this.searchKeyword.trim(),
				page: this.page,
				showConfirmed: this.showConfirmed,
				showUnconfirmed: this.showUnconfirmed
			})
			hideLoading()

			if (result?.code === 200) {
				this.paymentList = result?.msg?.list || []
				this.isSearch = result?.msg?.isSearch === true
				this.totalCount = result?.msg?.totalCount || 0
				this.pageSize = result?.msg?.pageSize || this.pageSize
				this.isLoaded = true
				if (isManual === true) {
					this.$message.success('刷新成功')
				}
			} else {
				this.$message.error(`获取支付列表失败${result?.code ? `（${result.code}）` : ''}`)
			}
		},
		async onRefreshBtnClicked() {
			await this.getPaymentList(true)
		},
		getListTitle() {
			if (this.isSearch === true) {
				return `搜索结果（${this.totalCount}）`
			}
			return `支付记录（${this.totalCount}）`
		},
		/**
		 * 空列表的原因不止一种。两个勾都去掉时列表本来就该是空的，
		 * 这时候说"还没有支付记录"会让人以为库里没数据。
		 */
		getEmptyText() {
			if (this.showConfirmed !== true && this.showUnconfirmed !== true) {
				return '请至少勾选一种支付状态'
			}
			if (this.isSearch === true) {
				return '没有搜到匹配的支付记录'
			}
			return '还没有支付记录'
		},
		async onFilterChanged() {
			// 换了筛选条件，之前那一页的页码就没意义了
			this.page = 1
			await this.getPaymentList()
		},
		async onPageChanged(page) {
			this.page = page
			await this.getPaymentList()
		},
		async onSearchClicked() {
			// 换了关键字，之前那一页的页码就没意义了
			this.page = 1
			await this.getPaymentList()
		},
		async onResetSearchClicked() {
			if (!this.searchKeyword) {
				return
			}
			this.searchKeyword = ''
			this.page = 1
			await this.getPaymentList()
		},
		formatTimeString(timestamp) {
			if (!timestamp) {
				return '无'
			}
			return moment(parseInt(timestamp) * 1000).tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm:ss');
		},
		/**
		 * 支付ID 只留头尾各四位，中间用 **** 代替。
		 *
		 * 完整的 uuid 一行放不下，在窄屏上会折成两行、把整张卡片撑高。
		 * 短到 8 位以内的就原样显示——再遮就只剩星号了。
		 * 注意这只是显示层的处理，复制按钮拿的仍是完整值，不然搜不回来。
		 */
		maskPaymentId(paymentId) {
			if (!paymentId) {
				return '无'
			}
			if (paymentId.length <= 8) {
				return paymentId
			}
			return `${paymentId.slice(0, 4)}****${paymentId.slice(-4)}`
		},
		/**
		 * 金额。跟统计页一致，amount 按"分"存，展示时除以 100 保留两位。
		 * 加密支付那条线后端已经把元换算成分，这里不用再分情况。
		 */
		getAmountText(item) {
			const amount = Number(item?.amount)
			if (!isFinite(amount)) {
				return '金额未知'
			}
			return `${(amount / 100).toFixed(2)} ${item?.currency || 'CNY'}`
		},
		/**
		 * 确认框里的文案。把这笔的金额和付款人写进去，
		 * 一屏十几条记录，光说"确认修改"根本分不清点的是哪一笔。
		 */
		getToggleConfirmText(item) {
			const target = item?.isConfirmed === true ? '未支付' : '已支付'
			const who = item?.email ? `（${item.email}）` : ''
			return `确认把 ${this.getAmountText(item)}${who} 这笔订单设为${target}？`
		},
		/**
		 * "设为未支付并 Block"的确认文案。这一下会动三样东西，
		 * 逐条写出来——比"确认执行？"更能让人在按之前反应过来。
		 */
		getBlockConfirmText(item) {
			const who = item?.email || '该付款人'
			return `确认将这笔 ${this.getAmountText(item)} 的订单设为未支付，`
				+ `并封禁 ${who} 及其名下所有设备、清空其会员时长？`
		},
		/**
		 * 退单 + 封人 + 清会员，一次做完。
		 *
		 * 三步在后端顺序执行，中间某一步做不了（比如订单上没记邮箱）也不回滚前面的，
		 * 所以这里按后端回的实际结果提示，不能笼统说一句"操作成功"。
		 */
		async onMarkUnpaidAndBlock(item) {
			if (!item?.paymentId || this.updatingPaymentId) {
				return
			}
			this.updatingPaymentId = item.paymentId
			try {
				const result = await postRequestAsync('admin/payment/markUnpaidAndBlock', {
					paymentId: item.paymentId,
					source: item.source
				})
				if (result?.code === 200) {
					item.isConfirmed = false

					if (result?.msg?.blocked === true) {
						const deviceCount = result?.msg?.deviceCount || 0
						this.$message.success(
							`已设为未支付，并封禁付款人${deviceCount > 0 ? `及 ${deviceCount} 台设备` : ''}、清空会员`
						)
					} else {
						// 订单改了，但账号没找到——这一半必须说出来，否则会以为人已经封了
						this.$message.warning('订单已设为未支付，但没找到对应账号，未能封禁和清会员')
					}

					// 当前筛掉了未支付的话，这一行留着就和筛选条件矛盾了
					if (this.showUnconfirmed !== true) {
						await this.getPaymentList()
					}
				} else if (result?.code === 303) {
					this.$message.error('找不到这笔订单')
				} else {
					this.$message.error(`操作失败${result?.code ? `（${result.code}）` : ''}`)
				}
			} finally {
				this.updatingPaymentId = null
			}
		},
		/**
		 * 人工改一笔订单的支付状态。
		 *
		 * 成功后只改本地这一行，不整页重拉：翻到第 5 页改一笔就跳回列表顶部很难用。
		 * 但如果当前筛掉了改后的状态（比如只看未支付、把它设成了已支付），
		 * 这一行留在列表里就和筛选条件矛盾了，这种情况才重拉。
		 */
		async onToggleConfirmed(item) {
			if (!item?.paymentId || this.updatingPaymentId) {
				return
			}
			const nextConfirmed = item.isConfirmed !== true
			this.updatingPaymentId = item.paymentId
			try {
				const result = await postRequestAsync('admin/payment/updatePaymentStatus', {
					paymentId: item.paymentId,
					source: item.source,
					isConfirmed: nextConfirmed
				})
				if (result?.code === 200) {
					item.isConfirmed = nextConfirmed
					this.$message.success(nextConfirmed ? '已设为已支付' : '已设为未支付')

					const isFilteredOut = (nextConfirmed && this.showConfirmed !== true)
						|| (!nextConfirmed && this.showUnconfirmed !== true)
					if (isFilteredOut) {
						await this.getPaymentList()
					}
				} else {
					this.$message.error(`操作失败${result?.code ? `（${result.code}）` : ''}`)
				}
			} finally {
				this.updatingPaymentId = null
			}
		},
		/**
		 * 复制任意一段文本。label 只用来拼提示语（"支付ID已复制"），
		 * 复制失败一律提示手动选中——浏览器在非安全上下文里会直接拒绝写剪贴板。
		 */
		async onCopyText(text, label) {
			if (!text) {
				return
			}
			const ok = await copyToClipboard(text)
			if (ok) {
				this.$message.success(`${label}已复制`)
			} else {
				this.$message.error('复制失败，请手动选中复制')
			}
		}
	}
}
</script>

<style scoped>
.payment-page-container {
	min-height: 100vh;
	position: relative;
	background-color: #191123;
	overflow-x: hidden;
	width: 100vw;
	padding: 1em 0;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
}

.payment-search-container {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	width: 100%;
	box-sizing: border-box;
	padding: 0 1em;
	gap: 0.6em;
}

.payment-search-input {
	flex: 1;
	/* flex 子项默认 min-width:auto，输入框会被内容撑开把按钮挤出屏幕 */
	min-width: 0;
	max-width: 30em;
	color: #E6D5B8;
	background-color: transparent;
	border: 1px solid rgba(230, 213, 184, 0.6);
	border-radius: 0.2em;
	/* App.vue 里有全局的 input {padding: 0 !important}，不加 !important 撑不开 */
	padding: 0.2em 0.8em !important;
	font-size: 1.2em;
	line-height: 1.2em;
	outline: none;
}

.payment-search-input:focus {
	border-color: #FF8900;
}

.payment-search-input::placeholder {
	color: rgba(230, 213, 184, 0.4);
}

/* 标题 | 分页 | 刷新 三段，窄屏放不下时整行换行 */
.btn-container {
	flex-wrap: wrap;
	row-gap: 0.4em;
	padding: 0 1em;
	margin-bottom: 1em;
}

/* 分页 / 搜索框 / 按钮统一对齐工单页的类目切换按钮：
   同为 font-size 1.2em，高度 = 行高 1.2em + 上下各 0.2em 内边距 + 1px 边框 */
.btn-container :deep(.ant-pagination) {
	font-size: 1.2em;
}

.btn-container :deep(.ant-pagination-simple .ant-pagination-prev),
.btn-container :deep(.ant-pagination-simple .ant-pagination-next),
.btn-container :deep(.ant-pagination-simple .ant-pagination-simple-pager) {
	height: calc(1.6em + 2px);
	line-height: calc(1.6em + 2px);
	min-width: calc(1.6em + 2px);
	margin-inline-end: 0.4em;
}

/* 翻页组最后一项的尾随间距要去掉，否则这一行的右边比其它行少 0.4em，看着不齐 */
.btn-container :deep(.ant-pagination-simple .ant-pagination-next) {
	margin-inline-end: 0;
}

.btn-container :deep(.ant-pagination-simple .ant-pagination-item-link) {
	display: flex;
	align-items: center;
	justify-content: center;
	height: calc(1.6em + 2px);
	min-width: calc(1.6em + 2px);
	border-radius: 0.2em;
	font-size: 1em;
}

.btn-container :deep(.ant-pagination-simple .ant-pagination-simple-pager input) {
	height: calc(1.6em + 2px);
	/* 页码框不画边框，只留数字；宽度收窄到刚好放下页码。
	   App.vue 里有全局的 .ant-pagination.ant-pagination-simple input {border: 1px ... !important}，
	   不加 !important 盖不掉 */
	border: none !important;
	box-shadow: none;
	background-color: transparent;
	min-width: 2em;
	border-radius: 0.2em;
	font-size: 1em;
	line-height: 1.2em;
	text-align: center;
	/* App.vue 里有全局的 input {padding: 0 !important}，不加 !important 撑不开 */
	padding: 0 0.2em !important;
	margin-right: 0.4em;
}

/* 全局 .header-btn 比搜索框矮一截，这里统一成和输入框一样的高度 */
.payment-search-btn {
	font-size: 1.2em;
	line-height: 1.2em;
	padding: 0.2em 1em;
	border-radius: 0.2em;
	white-space: nowrap;
}

.payment-page-title {
	color: #E6D5B8;
	font-size: 1.2em;
	padding: 0.8em 0;
}

/* 筛选行跟在分页行下面，左右内边距和搜索行、列表保持一致 */
.payment-filter-container {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 1.2em;
	width: 100%;
	box-sizing: border-box;
	padding: 0 1em;
	margin-bottom: 1em;
}

/* antd 的 checkbox 文字默认是深色的，在这套深色底上要改过来 */
.payment-filter-container :deep(.ant-checkbox-wrapper) {
	color: #E6D5B8;
	font-size: 1em;
}

@media only screen and (max-width: 900px) {
	.payment-search-container {
		padding: 0 1.2em;
		gap: 0.5em;
	}

	.btn-container {
		padding: 0 1.2em;
	}

	.payment-search-btn {
		padding: 0.2em 0.7em;
	}

	.payment-filter-container {
		padding: 0 1.2em;
	}
}

.payment-empty {
	color: rgba(230, 213, 184, 0.6);
	padding: 2em 0;
	text-align: center;
	width: 100%;
}

/* item-container 是全局样式，这里只补内边距；#app 上有全局的 text-align: center，
   不显式改回来每一行都会居中 */
.payment-row {
	padding: 0.8em;
	cursor: default;
	text-align: left;
	width: 100%;
	/* 不加 min-width:0，长支付ID会把卡片撑出屏幕 */
	min-width: 0;
}

/* 左边金额/方式/状态，右边"设为已支付"按钮。按钮靠上对齐，
   左边那堆标签在窄屏上换行变高时，按钮也还是贴在卡片右上角。

   width: 100% 不能省：全局 .item-container 上有 align-items: start，
   这一行会收缩成内容宽度，space-between 就把按钮顶不到行末了 */
.payment-head-line {
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	justify-content: space-between;
	gap: 0.5em;
	width: 100%;
	min-width: 0;
	margin-bottom: 0.3em;
}

.payment-head-info {
	display: flex;
	flex-direction: row;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.5em;
	min-width: 0;
	flex: 1;
}

/* 两个按钮竖着还是横着排由宽度决定，窄屏上会换行 */
.payment-btn-group {
	display: flex;
	flex-direction: row;
	align-items: center;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: 0.5em;
	flex: 0 0 auto;
}

.payment-toggle-btn {
	flex: 0 0 auto;
	font-size: 0.9em;
	padding: 0.3em 0.6em;
	border-radius: 0.3em;
	white-space: nowrap;
}

/* 设为已支付是加钱的方向，设为未支付是撤销，颜色上分开，别点错。
   实心底色 + 页面底色的字，跟旁边的描边状态标签拉开层次，一眼能认出这个是能按的 */
.payment-toggle-btn.is-to-paid {
	color: #191123;
	background-color: #52c41a;
	border-color: #52c41a;
}

.payment-toggle-btn.is-to-unpaid {
	color: #191123;
	background-color: #ff6b6b;
	border-color: #ff6b6b;
}

/* 退单 + 封人 + 清会员，一下动三样东西，用最深的红把它和旁边只改状态的按钮区分开 */
.payment-toggle-btn.is-block {
	color: #E6D5B8;
	background-color: #a8071a;
	border-color: #a8071a;
}

/* 请求还在路上时不让再点第二次 */
.payment-toggle-btn.is-updating {
	opacity: 0.5;
	pointer-events: none;
}

.payment-status-tag {
	font-size: 0.9em;
	padding: 0.1em 0.5em;
	border: 1px solid currentColor;
	border-radius: 0.3em;
	white-space: nowrap;
}

.payment-status-tag.is-paid {
	color: #52c41a;
}

.payment-status-tag.is-unpaid {
	color: rgba(230, 213, 184, 0.5);
}

.payment-amount {
	color: #E6D5B8;
	font-size: 1.2em;
	white-space: nowrap;
}

.payment-method {
	color: rgba(230, 213, 184, 0.6);
	font-size: 0.9em;
	padding: 0.1em 0.5em;
	border: 1px solid rgba(230, 213, 184, 0.4);
	border-radius: 0.3em;
	white-space: nowrap;
}

.payment-refund-tag {
	color: #ff6b6b;
	font-size: 0.9em;
	padding: 0.1em 0.5em;
	border: 1px solid currentColor;
	border-radius: 0.3em;
	white-space: nowrap;
}

.payment-field-line {
	display: flex;
	flex-direction: row;
	align-items: center;
	color: rgba(230, 213, 184, 0.85);
	font-size: 0.95em;
	margin-top: 0.2em;
	gap: 0.4em;
	min-width: 0;
	word-break: break-all;
}

.payment-field-label {
	color: rgba(230, 213, 184, 0.5);
	/* 固定宽度让右边的值竖着对齐 */
	flex: 0 0 5em;
}

.payment-id-text {
	word-break: break-all;
}

.copy-text-btn {
	width: 1em;
	height: 1em;
	flex: 0 0 auto;
	cursor: pointer;
	opacity: 0.7;
}

.copy-text-btn:hover {
	opacity: 1;
}
</style>
